import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@/app/routes'
import styles from './ForgotPassword.module.scss'
import { Card } from '@/ui/card'
import PlatformBranding from '../../components/PlatformBranding/PlatformBranding'
import DynamicForm from '../../components/DynamicForm/DynamicForm'
import { useUIConfig } from '../../hooks/useUIConfig'
import { toast } from 'sonner'
import FormHeading from '../../components/FormHeading/FormHeading'
import { useCheckUser } from '../../hooks/useCheckUser'
import { useSendOTP } from '../../hooks/useSendOTP'

const forgotPasswordFields = [
    {
        id: "email",
        label: "Email",
        type: "email",
        placeholder: "Enter your email",
        required: true,
        isDisplay: true,
        validation: {
            pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
            message: "Please enter a valid email address",
        },
    },
]

const ForgotPassword = () => {
    const { data: uiConfig, isLoading, error } = useUIConfig()
    const navigate = useNavigate()

    useEffect(() => {
        // This page may also show feedback from an async payload in the future.
    }, [])

    const checkUserMutation = useCheckUser()
    const sendOTPMutation = useSendOTP()

    const handleSubmit = async (data: any) => {
        const identifier = data.email
        const identifier_type = identifier.includes('@') ? 'email' : 'mobile'

        try {
            const checkResponse: any = await checkUserMutation.mutateAsync({ identifier, identifier_type })

            const exists = checkResponse?.success || checkResponse?.exists || checkResponse?.data
            if (!exists) {
                toast.error(checkResponse?.message || 'User not found.');
                return
            }

            await sendOTPMutation.mutateAsync({
                identifier,
                channel: 'EMAIL',
                purpose: 'PASSWORD_RESET',
            })

            toast.success('OTP sent to your email. Please check your inbox.')
            navigate(`/${ROUTES.OTP}`, { state: { identifier } })
        } catch (err: any) {
            toast.error(err?.message || 'An error occurred while sending OTP.');
        }
    }

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loader}>
                    <div className={styles.spinner}></div>
                    <p>Loading...</p>
                </div>
            </div>
        )
    }
    if (error) return <div>Error loading config</div>

    return (
        <div className={styles.container}>
            <div className={styles.wrapper}>
                <div className={styles.left}>
                    <PlatformBranding
                        logo={uiConfig?.data?.logo}
                        platformName={uiConfig?.data?.ui_config.platform_name}
                        slogan={uiConfig?.data?.ui_config.platform_slogan}
                    />
                </div>
                <div className={styles.right}>
                    <Card className={styles.form}>
                        <FormHeading
                            title="Forgot Password"
                            subtitle="Enter your email address and we will send you a link to reset your password."
                            as="h1"
                            align="center"
                        />
                        <DynamicForm
                            fields={forgotPasswordFields}
                            onSubmit={handleSubmit}
                            loading={false}
                            primaryAction={{ label: 'Send Reset Link' }}
                            options={{ showBackToLogin: true, loginLink: '/' }}
                        />
                    </Card>
                </div>
            </div>
        </div>
    )
}

export default ForgotPassword;